<script lang="ts">
	import type { PortableTextBlock } from "@portabletext/types";
	// import PortableTextContent from "#lib/portableText/PortableTextContent.svelte";
	import { PortableText } from "@portabletext/svelte";
	import { contentBlockComponents } from "@ishavskraft/shared";

	interface Props {
		title?: string | null;
		content: PortableTextBlock[];
		center?: boolean;
		bookStyle?: boolean;
		children?: import("svelte").Snippet;
		below?: import("svelte").Snippet;
	}

	let {
		title = null,
		content,
		center = false,
		bookStyle = false,
		children,
		below,
	}: Props = $props();
</script>

<article class:center class:book-style={bookStyle}>
	{#if title}
		<h1 class="px-edge-x max-w-prose-edge-x">{title}</h1>
	{/if}

	{@render children?.()}

	<!-- <PortableTextContent {content} --line-height="1.5" /> -->

	{#if content?.length}
		<div class="contents">
			<span
				class="pre-content siblings-prose siblings-w-prose-edge-x siblings-max-w-full siblings-px-edge-x"
			></span>

			<PortableText
				value={content}
				onMissingComponent={false}
				components={contentBlockComponents}
			/>
		</div>
	{/if}

	{@render below?.()}
</article>

<style lang="postcss">
	article :global(:where(.image-component-container)) {
		margin-block: var(--s-6);
	}

	article :global(:where(h2, * h2)) {
		font-size: var(--font-size-h2);
		margin-block-start: calc(var(--space) * 3);
	}
	article :global(:where(h3, * h3)) {
		font-size: var(--font-size-lead);
	}
	article :global(:where(h4, * h4)) {
		font-size: var(--font-size-body);
	}
	article :global(:not(:where(h2)) + :where(h3, * h3)) {
		margin-block-start: calc(var(--space) * 3);
	}
	article :global(:not(:where(h3)) + :where(h4, * h4)) {
		margin-block-start: calc(var(--space) * 2);
	}

	/* Progressive enhancement: even out multi-line headings, kill paragraph
	   orphans. No-op where unsupported. */
	article :global(:where(h2, h3, h4, * h2, * h3, * h4)) {
		text-wrap: balance;
	}
	article :global(p) {
		text-wrap: pretty;
	}

	/* Center variant */

	article.center h1,
	article.center :global(.pre-content ~ *) {
		margin-inline: auto;
	}

	/* Book style variant */

	.book-style :global(.pre-content ~ p),
	.book-style :global(.pre-content ~ * p) {
		margin-block-end: 0;
	}

	.book-style :global(.pre-content ~ p + p),
	.book-style :global(.pre-content ~ * p + p) {
		margin-block-start: 0;
		text-indent: var(--space-inline);
	}

	/* Pre-content */

	/* line-height */
	.pre-content ~ :global(:not(h2, h3, h4)) {
		line-height: 1.5;
	}

	/* Body headings need tighter leading than the 1.5 body leading set above.
	   Element-form selectors (0,1,1) to out-specify `.pre-content ~ *` (0,1,0). */
	.pre-content ~ :global(* h2),
	.pre-content ~ :global(* h3),
	.pre-content ~ :global(* h4) {
		line-height: var(--line-height);
	}

	/* lists */
	.pre-content ~ :global(* li) {
		margin-block: var(--s-1);
	}

	.pre-content ~ :global(:where(ol, ul) :where(ol, ul)) {
		margin-block: 0;
	}

	.pre-content ~ :global(ol) {
		list-style: inside decimal;
	}

	.pre-content ~ :global(ul) {
		list-style: inside disc;
	}

	.pre-content ~ :global(:where(ol, ul) ol) {
		padding-inline-start: var(--space-inline);
		list-style: decimal;
	}

	.pre-content ~ :global(:where(ol, ul) ul) {
		padding-inline-start: var(--space-inline);
		list-style: disc;
	}

	@media (--laptop) {
		.pre-content ~ :global(ol) {
			list-style: decimal;
		}
		.pre-content ~ :global(ul) {
			list-style: disc;
		}
	}
</style>
